import { FreeSpinOverlayEvent, ReelEvent, SlingoEvent, TestEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { SoundController } from "@lib/titan2-slingo/dist/game/controller/SoundController";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import gsap from "gsap";
import { GameSoundEvent } from "./event/GameSoundEvent";
import { ScatterPickEvent } from "./event/GameEvent";
/**
 * GameSoundController.ts
 */
export class GameSoundController extends SoundController {
    constructor() {
        super(...arguments);
        this.payLadderSoundsPlaying = {};
        this.musicBlendTime = 0.333; // in seconds
    }
    postCreate() {
        super.postCreate();
        //This may resolve ios audio cut-out issues
        document.addEventListener('visibilitychange', () => {
            const { ctx } = Howler;
            if (ctx && !document.hidden) {
                setTimeout(() => {
                    ctx.resume();
                }, 100);
            }
        }, false);
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerCore(CommonGameEvent.STAKE_SELECTOR_OPEN, this.handleButtonClick);
        this.registerCore(CommonGameEvent.STAKE_SELECTOR_CLOSE, this.handleButtonClick);
        this.registerCore(CommonGameEvent.OPTIONS_MENU_OPEN, this.handleButtonClick);
        this.registerCore(CommonGameEvent.OPTIONS_MENU_CLOSE, this.handleButtonClick);
        this.registerGame(SlingoEvent.PAY_TABLE_OPEN, this.handleButtonClick);
        this.registerGame(SlingoEvent.PAY_TABLE_CLOSE, this.handleButtonClick);
        this.registerGame(SlingoEvent.REEL_STRIKE_BONUS, this.handleReelStrikeBonus);
        this.registerGame(SlingoEvent.FREE_SPIN_AWARD, this.handleFreeSpinCollect);
        this.registerGame(ReelEvent.REEL_STOPPED, this.handleReelsComplete);
        this.registerGame(CommonGameEvent.REPLAY_START, this.handlePurchaseEntryRequest);
        this.registerGame(GameSoundEvent.WIN_LINE_INCREASE, this.handleWinLineIncrease);
        this.registerGame(GameSoundEvent.WIN_LINE_COMPLETE, this.handleWinLineComplete);
        this.registerGame(GameSoundEvent.WIN_LINE_FIREWORK, this.handleWinLineFirework);
        this.registerGame(GameSoundEvent.SUSPENSE_WIN_LINE, this.handleWinLineSuspense);
        this.registerGame(GameSoundEvent.WIN_LINE_COUNT_UP, this.handleWinLineCountUp);
        this.registerGame(GameSoundEvent.WIN_LINE_COUNT_UP_END, this.handleWinLineCountUpEnd);
        this.registerGame(GameSoundEvent.SCATTER_SYMBOL_WIN, this.handleScatterSymbolWin);
        this.registerGame(GameSoundEvent.FIREWORK_LOOP, this.handleFireworkLoop);
        this.registerGame(GameSoundEvent.FIREWORK_LOOP_END, this.handleFireworkLoopEnd);
        this.registerGame(ScatterPickEvent.TRANSITION_OUT, this.handleScatterTransitionOut);
        this.registerGame(FreeSpinOverlayEvent.FREE_SPIN_END, this.handleFreeSpinAdded);
        this.registerGame(TestEvent.SLINGO_1, this.handleSlingoSingle);
        this.registerGame(TestEvent.SLINGO_2, this.handleSlingoDouble);
        this.registerGame(TestEvent.SLINGO_3, this.handleSlingoTriple);
        this.registerGame(TestEvent.SLINGO_4, this.handleSlingoQuadruple);
        for (let i = 1; i <= 12; i++) {
            this.registerGame(SlingoEvent.getPatternEvent(i), () => this.handleSlingoPayLadder(i));
        }
        this.registerGame(CommonGameEvent.REPLAY_FINISHED, this.stopBackground);
        this.registerGame(SlingoEvent.SLINGO_WIN_ANIMATION, this.handleSlingoWinAnimation);
        this.registerGame(GameSoundEvent.DAB_SWOOSH, this.handleDabSwoosh);
        this.registerGame(SlingoEvent.FULL_HOUSE_APPEAR, this.handleFullHouseAppear);
        this.registerGame(GameSoundEvent.LADDER_NOTE, this.handleLadderNote);
        this.unregisterGame(SlingoEvent.REELS_COMPLETE);
    }
    handleDabSwoosh() {
        const startColumn = this.baseModel.getLatestMatchedSymbolColumn();
        this.playSound(this.getSoundPropertiesFromId(`DAB_SWOOSH_${startColumn}`)).volume(0.45);
    }
    handleButtonClick() {
        this.playSound(this.getSoundPropertiesFromId("NULL_CLICK"));
    }
    handleScatterWinAppear() {
        super.handleScatterWinAppear();
        this.playSound(this.getSoundPropertiesFromId("GAME_SUMMARY_WIN_4")).volume(1);
        if (this.backgroundSound) {
            this.fadeSoundTo(this.backgroundSound, 0.1, .5);
        }
    }
    handleScatterWinDisappear() {
        super.handleScatterWinDisappear();
        if (this.backgroundSound) {
            this.fadeSoundTo(this.backgroundSound, 1, .5);
        }
    }
    handleSlingoWinAnimation() {
        this.playSound(this.getSoundPropertiesFromId(`SLINGO_APPEAR`));
    }
    playBackground() {
        if (this.openingJingleSound) {
            this.fadeSoundTo(this.openingJingleSound, 0, 0.2).then(() => {
                this.stopSound(this.getSoundPropertiesFromId("GAME_READY"));
                this.openingJingleSound = undefined;
            });
        }
        let properties = this.getBackground();
        if (properties != this.background) {
            const switchingMusicTracks = this.backgroundSound !== undefined;
            this.stopBackground();
            this.backgroundSound = this.playSound(properties);
            this.backgroundSound.volume(0);
            if (switchingMusicTracks) {
                this.fadeSoundTo(this.backgroundSound, 1, this.musicBlendTime);
            }
            else {
                this.backgroundSound.volume(1);
            }
            this.background = properties;
        }
    }
    stopBackground() {
        if (this.background) {
            const oldBackgroundProperties = this.background;
            const oldBackgroundSound = this.backgroundSound;
            this.fadeSoundTo(oldBackgroundSound, 0, this.musicBlendTime).then(() => {
                this.stopSound(oldBackgroundProperties);
            });
            this.background = undefined;
            this.backgroundSound = undefined;
        }
    }
    fadeSoundTo(sound, toVolume, duration) {
        return new Promise(resolve => {
            const volume = { value: sound.volume() };
            return gsap.timeline().to(volume, {
                value: toVolume,
                onUpdate: () => {
                    sound.volume(volume.value);
                },
                onComplete: () => {
                    resolve();
                },
                duration
            });
        });
    }
    getBackground() {
        const matchedPatterns = this.model.getCurrentMatchedPatterns();
        if (matchedPatterns > 8) {
            return this.getSoundPropertiesFromId("BACKGROUND_3");
        }
        if (matchedPatterns > 3) {
            return this.getSoundPropertiesFromId("BACKGROUND_2");
        }
        return this.getSoundPropertiesFromId("BACKGROUND_1");
    }
    handleGameSummary() {
        this.stopBackground();
        const totalStaked = this.model.getTotalStaked();
        const totalWin = this.model.getTotalWin();
        const result = this.model.getResultData();
        if (result.hasNoWin()) {
            this.playSound(this.getSoundPropertiesFromId("GAME_OVER")).volume(0.9);
        }
        else if (result.isLowWin() || totalWin <= totalStaked) { // below stake
            this.playSound(this.getSoundPropertiesFromId("GAME_SUMMARY_WIN_0")).volume(0.9);
        }
        else if (result.isSmallWin()) {
            this.playSound(this.getSoundPropertiesFromId("GAME_SUMMARY_WIN_1")).volume(0.9);
        }
        else if (result.isBigWin() || result.isFullHouse()) {
            this.playSound(this.getSoundPropertiesFromId("GAME_SUMMARY_WIN_3")).volume(0.9);
        }
        else { // normal win
            this.playSound(this.getSoundPropertiesFromId("GAME_SUMMARY_WIN_2")).volume(0.9);
        }
    }
    getDab(numberOfDabs) {
        return this.getSoundPropertiesFromId(`DAB_SUCCESS_${numberOfDabs - 1}`);
    }
    getLevelUp(matchedPatterns) {
        if (matchedPatterns < 3) {
            return this.getSoundPropertiesFromId("PAYLADDER_NO_PRIZE");
        }
        else if (matchedPatterns < 11) {
            return this.getSoundPropertiesFromId("PAYLADDER_CASH_PRIZE");
        }
        return this.getSoundPropertiesFromId("PAYLADDER_FEATURE_PRIZE");
    }
    handleSlingoPayLadder(matches) {
        const properties = this.getLevelUp(matches);
        if (this.payLadderSoundsPlaying[properties.name]) {
            return;
        }
        const sound = this.playSound(properties);
        this.payLadderSoundsPlaying[properties.name] = true;
        sound.once("end", () => {
            this.payLadderSoundsPlaying[properties.name] = false;
        });
    }
    handleLevelUp() {
        // noop
    }
    handleLoaderRemove() {
        this.loaderClosed = true;
        this.playSound(this.getSoundPropertiesFromId("BACKGROUND_IDLE"));
        const sound = this.playSound(this.getSoundPropertiesFromId("GAME_READY"));
        sound.volume(0.9);
        sound.once("end", () => {
            if (this.gameInProgress) {
                this.openingJingleSound = undefined;
                this.playBackground();
            }
        });
        this.openingJingleSound = sound;
    }
    handleDabSuccess() {
        let ticketDab = this.model.getDabData();
        this.playSound(this.getDab(ticketDab.getNumberOfDabs())).volume(0.45);
    }
    handleAvailableWin() {
        if (this.loaderClosed) {
            this.playSound(this.getSoundPropertiesFromId("AVAILABLE_WIN")).volume(1);
        }
    }
    handleReelStrike() {
        super.handleReelStrike();
        const symbol = this.model.getReelDisplayData().getStopSymbol();
        const id = symbol.getId();
        if (!Number.isNaN(parseInt(id))) {
            if (this.idMatchesTicket(id)) {
                this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE_NUMBER_MATCH"));
            }
        }
    }
    handleReelStrikeBonus() {
        this.playSound(this.getSoundPropertiesFromId("REEL_STRIKE_BONUS"));
    }
    handleFreeSpinCollect() {
        this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_COLLECT"));
    }
    handleFreeSpinAdded() {
        this.playSound(this.getSoundPropertiesFromId("FREE_SPIN_ADDED"));
    }
    handleSuspenseSpin() {
        const stopIndex = this.model.getReelDisplayData().getStopIndex();
        if (stopIndex < 2)
            return;
        this.suspenseWinLineSound?.stop();
        this.suspenseWinLineSound = this.playSound(this.getSoundPropertiesFromId(`LINE_TENSION_${stopIndex - 1}`));
    }
    async handleScatterSymbolWin() {
        this.playSound(this.getSoundPropertiesFromId("PAYLADDER_CASH_PRIZE"));
        const music = this.playSound(this.getSoundPropertiesFromId("SCATTER_TRANSITION"));
        music.volume(0.9);
        // const musicComplete = new Promise((resolve) => music.once("end", resolve));
        if (this.backgroundSound) {
            await this.fadeSoundTo(this.backgroundSound, 0, .2);
        }
    }
    async handleScatterTransitionOut() {
        if (this.backgroundSound) {
            await this.fadeSoundTo(this.backgroundSound, 1, 1);
        }
    }
    handleReelsComplete() {
        const stopIndex = this.model.getReelDisplayData().getStopIndex();
        if (stopIndex < 4) {
            return;
        }
        this.suspenseSpinSound = undefined;
        super.handleReelsComplete();
        this.stopSound(this.getSoundPropertiesFromId("SUSPENSE_SPIN"));
        this.suspenseWinLineSound?.stop();
        this.suspenseWinLineSound = undefined;
    }
    handleWinLineSuspense() {
        const stopIndex = this.model.getReelDisplayData().getStopIndex();
        if (stopIndex < 2)
            return;
        this.suspenseWinLineSound?.stop();
        this.suspenseWinLineSound = this.playSound(this.getSoundPropertiesFromId(`LINE_TENSION_${stopIndex - 1}`));
    }
    handleWinLineIncrease() {
        const stopIndex = this.model.getReelDisplayData().getStopIndex();
        if (stopIndex < 2)
            return;
        if (stopIndex === 3) {
            this.winlineLoop = this.playSound(this.getSoundPropertiesFromId("LINE_WIN_LOOP"));
        }
        const index = this.baseModel.getReelDisplayData().getStopIndex();
        if (this.baseModel.isWildWinLine(index)) {
            this.playSound(this.getSoundPropertiesFromId(`WILD_LINE_WIN_${stopIndex - 2}`));
        }
        else {
            this.playSound(this.getSoundPropertiesFromId(`LINE_WIN_${stopIndex - 2}`));
        }
    }
    async handleWinLineComplete() {
        this.winlineLoop?.stop();
        this.playSound(this.getSoundPropertiesFromId(`LINE_WIN_END`)).volume(0.9);
        if (this.baseModel.getWinLine().getNumberOfSymbols() >= 4) {
            this.playSound(this.getSoundPropertiesFromId("PAYLADDER_CASH_PRIZE"));
            this.playSound(this.getSoundPropertiesFromId("GAME_SUMMARY_WIN_2")).volume(0.9);
            if (this.backgroundSound) {
                await this.fadeSoundTo(this.backgroundSound, 0, .2);
                await gsap.to(this, { duration: 6 });
                await this.fadeSoundTo(this.backgroundSound, 1, .5);
            }
        }
    }
    idMatchesTicket(id) {
        const ticketDab = this.model.getDabData();
        const autoHighlightCells = ticketDab.getAutoHighlightCells();
        return autoHighlightCells.findIndex(cell => cell.getNumber() === parseInt(id)) > -1;
    }
    handleWinLineFirework() {
        this.playSound(this.getSoundPropertiesFromId(`FIREWORK_${Math.floor(Math.random() * 5) + 1}`));
    }
    handleWinLineCountUp() {
        this.playSound(this.getSoundPropertiesFromId("SCATTER_WIN_COUNT"));
    }
    handleBigWinAnimationPhaseOne() {
        super.handleBigWinAnimationPhaseOne();
        if (this.model.getCurrentMatchedPatterns() >= 3) {
            this.bigWinTicketAnimation = this.playSound(this.getSoundPropertiesFromId("BIG_WIN_ANIM"));
            this.bigWinTicketAnimation.volume(0.5);
        }
    }
    handleBigWinAppear() {
        super.handleBigWinAppear();
        if (this.bigWinTicketAnimation) {
            this.fadeSoundTo(this.bigWinTicketAnimation, 0, .3);
        }
    }
    handleWinLineCountUpEnd() {
        this.stopSound(this.getSoundPropertiesFromId("SCATTER_WIN_COUNT"));
        this.playSound(this.getSoundPropertiesFromId("SCATTER_WIN_COUNT_END"));
    }
    handleBigWinCount() {
        super.handleBigWinCount();
        this.playSound(this.getSoundPropertiesFromId("CASH_LOOP"));
        this.playSound(this.getSoundPropertiesFromId("GAME_SUMMARY_WIN_4")).volume(1);
    }
    handleFullHouseAnimationPhaseOne() {
        super.handleFullHouseAnimationPhaseOne();
        this.playSound(this.getSoundPropertiesFromId("FULL_HOUSE_ANIM")).volume(0.5);
    }
    handleFullHouseAppear() {
        this.playSound(this.getSoundPropertiesFromId("GAME_SUMMARY_WIN_4")).volume(1);
    }
    handleBigWinCountEnd() {
        super.handleBigWinCountEnd();
        this.stopSound(this.getSoundPropertiesFromId("CASH_LOOP"));
    }
    handleFireworkLoop() {
        this.fireworkSound = this.playSound(this.getSoundPropertiesFromId("FIREWORK_LOOP"));
        this.fireworkSound.volume(0);
        this.fadeSoundTo(this.fireworkSound, 1, .5);
    }
    async handleFireworkLoopEnd() {
        if (!this.fireworkSound)
            return;
        await this.fadeSoundTo(this.fireworkSound, 0, .5);
        this.stopSound(this.getSoundPropertiesFromId("FIREWORK_LOOP"));
        this.fireworkSound = undefined;
    }
    handleLadderNote(event) {
        const { note } = event.getPayload();
        this.playSound(this.getSoundPropertiesFromId(`LADDER_NOTE_${note}`));
    }
}
//# sourceMappingURL=GameSoundController.js.map